/**
 * @fileoverview Text File Editor Module - Direct text file creation and editing
 * 
 * This module provides a rich text editor for creating and editing text files
 * directly in the knowledge base interface with auto-save, markdown support,
 * and keyboard shortcuts.
 * 
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useModuleContext, useModuleEvents } from '../context/ModuleContextProvider.jsx';
import { ModuleEventType } from '../types/index.js';
import { 
  TextFileEditorConfig, 
  validateTextEditorConfig,
  validateFilename,
  validateContent,
  sanitizeFilename,
  countWords,
  generateAutoSaveKey,
  KEYBOARD_SHORTCUTS
} from './types.js';
import { TextEditorInterface } from './components/TextEditorInterface.jsx';
import { TextEditorToolbar } from './components/TextEditorToolbar.jsx';
import { TextEditorPreview } from './components/TextEditorPreview.jsx';
import { CharacterCountDisplay } from './components/CharacterCountDisplay.jsx';
import { useAutoSave } from './hooks/useAutoSave.js';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts.js';
import { useMarkdownProcessor } from './hooks/useMarkdownProcessor.js';
import { textEditorService } from './services/TextEditorService.js';

/**
 * Text File Editor Module Component
 * 
 * @param {Object} props - Component props
 * @param {TextFileEditorConfig} props.config - Module configuration
 * @param {ModuleContext} props.context - Module context
 * @param {function(ModuleEvent): void} [props.onEvent] - Event handler
 * @param {string} [props.className] - CSS class name
 * @param {string} [props.testId] - Test ID
 */
const TextFileEditorModule = ({ 
  config, 
  context, 
  onEvent, 
  className = '', 
  testId = 'text-editor-module' 
}) => {
  // Validate configuration
  const configValidation = validateTextEditorConfig(config);
  if (!configValidation.valid) {
    throw new Error(`Invalid TextEditor configuration: ${configValidation.errors.join(', ')}`);
  }

  // Module context and events
  const moduleContext = useModuleContext();
  const { emit: emitEvent } = useModuleEvents('*', null, { enabled: false });

  // Module state
  const [moduleState, setModuleState] = useState({
    initialized: false,
    saving: false,
    error: null
  });

  // Editor state
  const [editorState, setEditorState] = useState({
    filename: config.initialFilename || '',
    content: config.initialContent || '',
    hasUnsavedChanges: false,
    showPreview: false,
    characterCount: (config.initialContent || '').length,
    wordCount: countWords(config.initialContent || ''),
    lastModified: new Date()
  });

  // Validation state
  const [validationState, setValidationState] = useState({
    filenameValid: true,
    filenameErrors: [],
    contentValid: true,
    contentErrors: [],
    contentWarnings: []
  });

  // Refs
  const textareaRef = useRef(null);
  const autoSaveKeyRef = useRef(null);

  // Custom hooks
  const autoSave = useAutoSave(config, editorState, moduleState.saving);
  const markdownProcessor = useMarkdownProcessor(config.enableMarkdown);

  // Initialize module
  useEffect(() => {
    try {
      // Generate auto-save key
      autoSaveKeyRef.current = generateAutoSaveKey(
        config.parrotId || 'global',
        config.initialFilename
      );

      // Load auto-saved content if available
      if (config.autoSave && config.mode === 'create') {
        const autoSavedContent = localStorage.getItem(autoSaveKeyRef.current);
        if (autoSavedContent) {
          try {
            const parsed = JSON.parse(autoSavedContent);
            setEditorState(prev => ({
              ...prev,
              content: parsed.content || prev.content,
              filename: parsed.filename || prev.filename,
              hasUnsavedChanges: true,
              characterCount: (parsed.content || '').length,
              wordCount: countWords(parsed.content || '')
            }));
          } catch (error) {
            console.warn('Failed to load auto-saved content:', error);
          }
        }
      }

      setModuleState(prev => ({ ...prev, initialized: true }));
      
      // Emit module loaded event
      emitEvent({
        type: ModuleEventType.MODULE_LOADED,
        payload: {
          moduleId: 'text-editor-module',
          mode: config.mode,
          autoSave: config.autoSave
        },
        source: 'text-editor-module',
        timestamp: Date.now()
      });

    } catch (error) {
      console.error('TextFileEditorModule initialization error:', error);
      setModuleState(prev => ({ 
        ...prev, 
        initialized: false, 
        error: error.message 
      }));
    }
  }, [config, emitEvent]);

  // Auto-save effect
  useEffect(() => {
    if (config.autoSave && editorState.hasUnsavedChanges && autoSaveKeyRef.current) {
      const autoSaveData = {
        filename: editorState.filename,
        content: editorState.content,
        timestamp: Date.now()
      };

      try {
        localStorage.setItem(autoSaveKeyRef.current, JSON.stringify(autoSaveData));
        
        // Call auto-save callback if provided
        if (config.onAutoSave) {
          config.onAutoSave(autoSaveData);
        }

        // Emit auto-save event
        emitEvent({
          type: ModuleEventType.MODULE_DATA_CHANGED,
          payload: {
            action: 'auto_save',
            filename: editorState.filename,
            contentLength: editorState.content.length
          },
          source: 'text-editor-module',
          timestamp: Date.now()
        });

      } catch (error) {
        console.warn('Auto-save failed:', error);
      }
    }
  }, [config, editorState.hasUnsavedChanges, editorState.filename, editorState.content, emitEvent]);

  /**
   * Handle content change
   */
  const handleContentChange = useCallback((newContent) => {
    // Validate content length
    if (newContent.length > config.maxLength) {
      context.toast.warning(`Content exceeds maximum length of ${config.maxLength} characters`);
      return;
    }

    // Validate content
    const contentValidation = validateContent(newContent, config.maxLength);
    
    setValidationState(prev => ({
      ...prev,
      contentValid: contentValidation.valid,
      contentErrors: contentValidation.errors,
      contentWarnings: contentValidation.warnings
    }));

    // Show warnings if any
    if (contentValidation.warnings.length > 0) {
      contentValidation.warnings.forEach(warning => {
        context.toast.warning(warning);
      });
    }

    // Update editor state
    setEditorState(prev => ({
      ...prev,
      content: newContent,
      hasUnsavedChanges: true,
      characterCount: newContent.length,
      wordCount: countWords(newContent),
      lastModified: new Date()
    }));

    // Call content change callback
    if (config.onContentChange) {
      config.onContentChange(newContent);
    }

    // Emit content change event
    emitEvent({
      type: ModuleEventType.MODULE_DATA_CHANGED,
      payload: {
        action: 'content_changed',
        contentLength: newContent.length,
        wordCount: countWords(newContent),
        hasUnsavedChanges: true
      },
      source: 'text-editor-module',
      timestamp: Date.now()
    });

  }, [config, context, emitEvent]);

  /**
   * Handle filename change
   */
  const handleFilenameChange = useCallback((newFilename) => {
    // Sanitize filename
    const sanitized = sanitizeFilename(newFilename);
    
    // Validate filename
    const filenameValidation = validateFilename(sanitized);
    
    setValidationState(prev => ({
      ...prev,
      filenameValid: filenameValidation.valid,
      filenameErrors: filenameValidation.errors
    }));

    // Show validation errors
    if (filenameValidation.errors.length > 0) {
      filenameValidation.errors.forEach(error => {
        context.toast.error(error);
      });
    }

    // Show warnings
    if (filenameValidation.warnings.length > 0) {
      filenameValidation.warnings.forEach(warning => {
        context.toast.warning(warning);
      });
    }

    // Update editor state
    setEditorState(prev => ({
      ...prev,
      filename: sanitized,
      hasUnsavedChanges: true,
      lastModified: new Date()
    }));

    // Call filename change callback
    if (config.onFilenameChange) {
      config.onFilenameChange(sanitized);
    }

    // Emit filename change event
    emitEvent({
      type: ModuleEventType.MODULE_DATA_CHANGED,
      payload: {
        action: 'filename_changed',
        filename: sanitized,
        valid: filenameValidation.valid
      },
      source: 'text-editor-module',
      timestamp: Date.now()
    });

  }, [config, context, emitEvent]);

  /**
   * Handle save action
   */
  async function handleSave() {
    if (moduleState.saving) return;

    try {
      // Validate before saving
      const filenameValidation = validateFilename(editorState.filename);
      const contentValidation = validateContent(editorState.content, config.maxLength);

      if (!filenameValidation.valid || !contentValidation.valid) {
        const errors = [...filenameValidation.errors, ...contentValidation.errors];
        context.toast.error(`Cannot save: ${errors.join(', ')}`);
        return;
      }

      setModuleState(prev => ({ ...prev, saving: true, error: null }));

      // Emit save start event
      emitEvent({
        type: ModuleEventType.USER_ACTION,
        payload: {
          action: 'save_started',
          filename: editorState.filename,
          contentLength: editorState.content.length
        },
        source: 'text-editor-module',
        timestamp: Date.now()
      });

      // Prepare save data
      const saveData = {
        filename: editorState.filename,
        content: editorState.content,
        mode: config.mode,
        metadata: {
          ...config.metadata,
          characterCount: editorState.characterCount,
          wordCount: editorState.wordCount,
          lastModified: editorState.lastModified
        },
        parrotId: config.parrotId
      };

      // Save using the service
      const result = await textEditorService.saveTextFile(saveData, config.saveEndpoint);

      // Update state on successful save
      setEditorState(prev => ({
        ...prev,
        hasUnsavedChanges: false
      }));

      // Clear auto-save data
      if (autoSaveKeyRef.current) {
        localStorage.removeItem(autoSaveKeyRef.current);
      }

      // Call save callback
      if (config.onSave) {
        await config.onSave(editorState.filename, editorState.content, result);
      }

      // Show success message
      context.toast.success(`File "${editorState.filename}.txt" saved successfully`);

      // Emit save completed event
      emitEvent({
        type: ModuleEventType.USER_ACTION,
        payload: {
          action: 'save_completed',
          filename: editorState.filename,
          fileId: result.file?.id,
          success: true
        },
        source: 'text-editor-module',
        timestamp: Date.now()
      });

    } catch (error) {
      console.error('Save error:', error);
      context.toast.error(`Failed to save file: ${error.message}`);
      
      setModuleState(prev => ({ ...prev, error: error.message }));

      // Emit save failed event
      emitEvent({
        type: ModuleEventType.MODULE_ERROR,
        payload: {
          action: 'save_failed',
          error: error.message,
          filename: editorState.filename
        },
        source: 'text-editor-module',
        timestamp: Date.now()
      });

    } finally {
      setModuleState(prev => ({ ...prev, saving: false }));
    }
  }

  /**
   * Handle cancel action
   */
  const handleCancel = useCallback(() => {
    // Clear auto-save data
    if (autoSaveKeyRef.current) {
      localStorage.removeItem(autoSaveKeyRef.current);
    }

    // Call cancel callback
    if (config.onCancel) {
      config.onCancel();
    }

    // Emit cancel event
    emitEvent({
      type: ModuleEventType.USER_ACTION,
      payload: {
        action: 'cancelled',
        hasUnsavedChanges: editorState.hasUnsavedChanges
      },
      source: 'text-editor-module',
      timestamp: Date.now()
    });

  }, [config, editorState.hasUnsavedChanges, emitEvent]);

  /**
   * Toggle preview mode
   */
  const togglePreview = useCallback(() => {
    setEditorState(prev => ({
      ...prev,
      showPreview: !prev.showPreview
    }));
  }, []);

  /**
   * Insert bold markdown
   */
  const insertBold = useCallback(() => {
    insertMarkdown('**', '**', 'bold text');
  }, []);

  /**
   * Insert italic markdown
   */
  const insertItalic = useCallback(() => {
    insertMarkdown('*', '*', 'italic text');
  }, []);

  /**
   * Insert markdown formatting
   */
  const insertMarkdown = useCallback((before, after, placeholder) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = editorState.content.substring(start, end);
    
    const replacement = selectedText || placeholder;
    const newContent = 
      editorState.content.substring(0, start) +
      before + replacement + after +
      editorState.content.substring(end);

    handleContentChange(newContent);

    // Set cursor position
    setTimeout(() => {
      const newStart = start + before.length;
      const newEnd = newStart + replacement.length;
      textarea.setSelectionRange(newStart, newEnd);
      textarea.focus();
    }, 0);
  }, [editorState.content, handleContentChange]);

  // Initialize keyboard shortcuts after functions are defined
  const keyboardShortcuts = useKeyboardShortcuts(config, {
    onSave: handleSave,
    onPreview: togglePreview,
    onBold: insertBold,
    onItalic: insertItalic
  });

  // Show error state if module failed to initialize
  if (!moduleState.initialized && moduleState.error) {
    return (
      <div className={`text-editor-module-error ${className}`} data-testid={`${testId}-error`}>
        <div className="error-content">
          <h3>Text Editor Module Error</h3>
          <p>{moduleState.error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while initializing
  if (!moduleState.initialized) {
    return (
      <div className={`text-editor-module-loading ${className}`} data-testid={`${testId}-loading`}>
        <div className="loading-content">
          <div className="loading-spinner" />
          <p>Initializing text editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`text-editor-module ${className}`} data-testid={testId}>
      {/* Editor Header */}
      <div className="editor-header">
        <div className="filename-input-container">
          <label htmlFor={`${testId}-filename`} className="filename-label">
            Filename:
          </label>
          <input
            id={`${testId}-filename`}
            type="text"
            value={editorState.filename}
            onChange={(e) => handleFilenameChange(e.target.value)}
            placeholder="Enter filename (without .txt extension)"
            disabled={config.disabled || moduleState.saving}
            className={`filename-input ${!validationState.filenameValid ? 'error' : ''}`}
            data-testid={`${testId}-filename-input`}
          />
          <span className="filename-extension">.txt</span>
        </div>

        {/* Toolbar */}
        {config.enableMarkdown && (
          <TextEditorToolbar
            onBold={insertBold}
            onItalic={insertItalic}
            onPreview={togglePreview}
            showPreview={editorState.showPreview}
            disabled={config.disabled || moduleState.saving}
            testId={`${testId}-toolbar`}
          />
        )}
      </div>

      {/* Editor Content */}
      <div className="editor-content">
        {editorState.showPreview && config.enablePreview ? (
          <TextEditorPreview
            content={editorState.content}
            markdownProcessor={markdownProcessor}
            testId={`${testId}-preview`}
          />
        ) : (
          <TextEditorInterface
            ref={textareaRef}
            content={editorState.content}
            onChange={handleContentChange}
            placeholder={config.placeholder}
            disabled={config.disabled || moduleState.saving}
            maxLength={config.maxLength}
            testId={`${testId}-textarea`}
          />
        )}
      </div>

      {/* Footer */}
      <div className="editor-footer">
        {/* Character Count */}
        {config.showCharacterCount && (
          <CharacterCountDisplay
            characterCount={editorState.characterCount}
            wordCount={config.showWordCount ? editorState.wordCount : null}
            maxLength={config.maxLength}
            testId={`${testId}-count`}
          />
        )}

        {/* Action Buttons */}
        <div className="editor-actions">
          <button
            type="button"
            onClick={handleCancel}
            disabled={config.disabled || moduleState.saving}
            className="cancel-button"
            data-testid={`${testId}-cancel`}
          >
            Cancel
          </button>
          
          <button
            type="button"
            onClick={handleSave}
            disabled={
              config.disabled || 
              moduleState.saving || 
              !validationState.filenameValid || 
              !validationState.contentValid ||
              !editorState.filename.trim()
            }
            className="save-button primary"
            data-testid={`${testId}-save`}
          >
            {moduleState.saving ? 'Saving...' : 'Save File'}
          </button>
        </div>
      </div>

      {/* Keyboard Shortcuts Help */}
      {config.enableKeyboardShortcuts && (
        <div className="keyboard-shortcuts-help" data-testid={`${testId}-shortcuts`}>
          <small>
            Shortcuts: {Object.entries(KEYBOARD_SHORTCUTS).map(([key, shortcut]) => 
              `${shortcut.description}`
            ).join(' • ')}
          </small>
        </div>
      )}
    </div>
  );
};

/**
 * Get default configuration for TextFileEditorModule
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6
 */
TextFileEditorModule.getDefaultConfig = () => ({
  mode: 'create',
  initialContent: '',
  initialFilename: '',
  maxLength: 50000, // 50,000 character limit per requirements 2.4
  placeholder: 'Enter your knowledge base content...',
  autoSave: true, // Auto-save functionality per requirements 2.3
  autoSaveInterval: 30000,
  showCharacterCount: true, // Character count display per requirements 2.4
  showWordCount: true,
  enableMarkdown: true, // Markdown support per requirements 2.2
  enablePreview: true,
  enableKeyboardShortcuts: true, // Keyboard shortcuts per requirements 2.6
  saveEndpoint: '/api/modules/text-editor/save',
  disabled: false
});

export default TextFileEditorModule;